import { type OperatorSlug, UnconfirmedReason } from '@ev/shared'
import type { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useColorScheme, usePrefersReducedMotion } from '../app/media'
import { useLocationBootstrap, useLocationStore } from '../app/useUserLocation'
import { useAvailabilityIndex, useStationIndex } from '../data/hooks'
import { type Strings, useStrings } from '../i18n'
import type { FilterFacet } from '../state/filters'
import { tallyStations, unresolvedFacets } from '../state/stationFilter'
import { selectFilters, selectQuery, selectSelectedSiteId, useAppStore } from '../state/store'
import { MAP_THEMES, siteBandFor } from '../theme/palette'
import { resolveBasemapStyleUrl } from './config'
import {
  EMPTY_LOCATION,
  INTERACTIVE_LAYER_IDS,
  type LocationCollection,
  selectionFilter,
  toCollection,
  toLocationCollection,
} from './layers'
import { MapKeyboardList } from './MapKeyboardList'
import { type HoveredSite, MapTooltip } from './MapTooltip'
import {
  createMap,
  easeToSite,
  ensureLocationLayers,
  ensureStationLayers,
  expandCluster,
  frameCountry,
  frameLocation,
  type MapHandle,
  readViewport,
  setLocationData,
  setSelectionFilter,
  setStationsData,
} from './mapInstance'
import { countInBounds, keyboardTargets, locationViewBounds, type ViewportBounds } from './viewport'
import './map.css'

/**
 * The map.
 *
 * Three things are worth knowing before reading the effects below.
 *
 * The GeoJSON is fetched once, through the shared cache, so the search box gets
 * the same copy. Every filter change is one pass over the features already in
 * memory followed by one `setData` on the source - never a reload. MapLibre does
 * the clustering for the new set on its own worker thread, so the main thread
 * only pays for the predicate pass and the hand-off.
 *
 * MapLibre itself arrives through a dynamic `import()`. The shell is interactive
 * before the engine has downloaded, and the initial JavaScript budget covers our
 * code rather than a vendor library.
 *
 * Nothing here reaches into the filter or detail panels. The store is the only
 * channel: filters and the selected site come in, results go out. That is why
 * clicking a marker opens the detail panel without the two components knowing
 * about each other.
 */

/** Pointer slop, in px, so a dot is clickable without being hit exactly. */
const HIT_SLOP = 5

/** How close to an edge the hover card has to get before it flips to the other side. */
const TOOLTIP_MARGIN = { x: 260, y: 130 }

/** Facet -> the heading the panel already has for it, for the live region. */
const FACET_LABEL_KEYS = {
  operators: 'operators',
  operatorNames: 'operators',
  connectors: 'connectors',
  currentTypes: 'currentType',
  voltageClasses: 'voltageClass',
  availability: 'availability',
  paymentMethods: 'paymentMethod',
} as const satisfies Record<FilterFacet, keyof Strings['filters']>

const pointCoordinates = (geometry: unknown): [number, number] | null => {
  if (typeof geometry !== 'object' || geometry === null) return null
  const candidate = geometry as { type?: unknown; coordinates?: unknown }
  if (candidate.type !== 'Point' || !Array.isArray(candidate.coordinates)) return null
  const [lon, lat] = candidate.coordinates as unknown[]
  return typeof lon === 'number' && typeof lat === 'number' ? [lon, lat] : null
}

const readHoveredSite = (properties: Record<string, unknown>): HoveredSite | null => {
  const { id, n, c, op, ac, dc, pmax, unc } = properties
  if (typeof id !== 'string' || typeof n !== 'string') return null

  const counts = {
    ac: typeof ac === 'number' ? ac : 0,
    dc: typeof dc === 'number' ? dc : 0,
    pmax: typeof pmax === 'number' ? pmax : 0,
  }

  return {
    id,
    name: n,
    city: typeof c === 'string' ? c : '',
    operator: (typeof op === 'string' ? op : 'other') as OperatorSlug,
    ...counts,
    band: siteBandFor(counts),
    // Absent on a settled site, so `null` is the answer for both "nothing is in
    // doubt" and "a reason this build has never heard of". Checked against the
    // runtype rather than cast: these come back off a MapLibre feature, which is
    // a bag of unknowns however carefully the artifact was validated on the way
    // in, and an unrecognised slug would index the badge copy with nothing.
    unconfirmed: UnconfirmedReason.test(unc) ? unc : null,
  }
}

export const MapCanvas = () => {
  const strings = useStrings()
  const scheme = useColorScheme()
  const reducedMotion = usePrefersReducedMotion()

  const filters = useAppStore(selectFilters)
  const query = useAppStore(selectQuery)
  const selectedSiteId = useAppStore(selectSelectedSiteId)
  const setSelectedSiteId = useAppStore((state) => state.setSelectedSiteId)
  const publishResults = useAppStore((state) => state.publishResults)

  const { resource, reload } = useStationIndex()
  const availabilityBySite = useAvailabilityIndex(filters.availability.length > 0)

  // The map opens on the visitor, so the fix is asked for on the first paint
  // rather than waited for behind a button.
  useLocationBootstrap()
  const position = useLocationStore((store) => store.position)
  const locationState = useLocationStore((store) => store.state)
  const fixes = useLocationStore((store) => store.fixes)
  const requestLocation = useLocationStore((store) => store.request)

  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const handleRef = useRef<MapHandle | null>(null)

  const [engineReady, setEngineReady] = useState(false)
  const [engineFailed, setEngineFailed] = useState(false)
  const [camera, setCamera] = useState<{ bounds: ViewportBounds; center: [number, number] } | null>(
    null,
  )
  const [hovered, setHovered] = useState<HoveredSite | null>(null)

  /** Set once the visitor pans or zooms by hand. Never reset. */
  const cameraTouchedRef = useRef(false)
  /** The fix count the camera was last framed for; 0 before the first one. */
  const framedFixesRef = useRef(0)

  const theme = MAP_THEMES[scheme]
  const index = resource.status === 'ready' ? resource.value : null

  // Values the imperative MapLibre handlers need to read *now* rather than
  // from the closure they were registered in.
  const themeRef = useRef(theme)
  themeRef.current = theme
  const selectedRef = useRef(selectedSiteId)
  selectedRef.current = selectedSiteId
  const reducedMotionRef = useRef(reducedMotion)
  reducedMotionRef.current = reducedMotion
  const schemeRef = useRef(scheme)
  const locationRef = useRef<LocationCollection>(EMPTY_LOCATION)
  locationRef.current = position === null ? EMPTY_LOCATION : toLocationCollection(position)
  const labelRef = useRef(strings.map.label)
  labelRef.current = strings.map.label
  const attributionRef = useRef(strings.map.dataAttribution)

  const tally = useMemo(
    () =>
      tallyStations(index?.features ?? [], filters, {
        query,
        haystackById: index?.haystackById,
        availabilityBySite,
      }),
    [index, filters, query, availabilityBySite],
  )
  const tallyRef = useRef(tally)
  tallyRef.current = tally

  const visible = useMemo(
    () => (camera === null ? tally.features.length : countInBounds(tally.features, camera.bounds)),
    [tally, camera],
  )

  const targets = useMemo(
    () => (camera === null ? [] : keyboardTargets(tally.features, camera.bounds, camera.center)),
    [tally, camera],
  )

  const ignoredFacets = useMemo(
    () => unresolvedFacets(filters, { query, availabilityBySite }),
    [filters, query, availabilityBySite],
  )

  // ------------------------------------------------------------------------
  // The map itself. Created once; every later change is applied in place.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const container = containerRef.current
    if (container === null) return

    let cancelled = false
    let created: MapLibreMap | null = null

    createMap({
      container,
      scheme: schemeRef.current,
      reducedMotion: reducedMotionRef.current,
      label: labelRef.current,
      dataAttribution: attributionRef.current,
    })
      .then((handle) => {
        // StrictMode mounts, unmounts and mounts again; the import may land
        // after the first teardown.
        if (cancelled) {
          handle.map.remove()
          return
        }
        const { map } = handle
        created = map
        mapRef.current = map
        handleRef.current = handle

        const syncLayers = (): void => {
          if (!map.isStyleLoaded()) return
          ensureStationLayers(
            map,
            themeRef.current,
            selectedRef.current,
            toCollection(tallyRef.current.features),
          )
          // Added here and not only on `load`: a basemap swap drops the blue dot
          // along with everything else, and a visitor who has just switched to
          // dark would be left hunting for themselves on the map.
          ensureLocationLayers(map, themeRef.current, locationRef.current)
        }

        map.on('load', () => {
          syncLayers()
          setCamera(readViewport(map))
          setEngineReady(true)
        })

        // A basemap swap drops every custom layer, so they are re-added here.
        map.on('styledata', syncLayers)
        map.on('moveend', () => setCamera(readViewport(map)))

        // A gesture carries the DOM event that caused it; a programmatic
        // `easeTo` does not. That is the whole test for "the visitor has
        // claimed the camera", which the opening location frame yields to.
        map.on('movestart', (event) => {
          if ('originalEvent' in event && event.originalEvent !== undefined)
            cameraTouchedRef.current = true
        })
        map.on('error', (event) => console.error('map: maplibre reported an error', event.error))

        map.on('click', (event: MapMouseEvent) => {
          const hits = map.queryRenderedFeatures(
            [
              [event.point.x - HIT_SLOP, event.point.y - HIT_SLOP],
              [event.point.x + HIT_SLOP, event.point.y + HIT_SLOP],
            ],
            { layers: INTERACTIVE_LAYER_IDS },
          )
          const hit = hits[0]

          // Clicking bare basemap is how you put the detail panel away.
          if (hit === undefined) {
            setSelectedSiteId(null)
            return
          }

          const center = pointCoordinates(hit.geometry)
          const clusterId = hit.properties.cluster_id

          if (typeof clusterId === 'number' && center !== null) {
            void expandCluster(map, clusterId, center, reducedMotionRef.current)
            return
          }

          const site = readHoveredSite(hit.properties)
          if (site !== null) setSelectedSiteId(site.id)
        })

        map.on('mousemove', (event: MapMouseEvent) => {
          const hits = map.queryRenderedFeatures(
            [
              [event.point.x - HIT_SLOP, event.point.y - HIT_SLOP],
              [event.point.x + HIT_SLOP, event.point.y + HIT_SLOP],
            ],
            { layers: INTERACTIVE_LAYER_IDS },
          )
          const hit = hits[0]
          map.getCanvas().style.cursor = hit === undefined ? '' : 'pointer'

          const tooltip = tooltipRef.current
          if (tooltip !== null) {
            tooltip.style.transform = `translate(${event.point.x}px, ${event.point.y}px)`
            const canvas = map.getCanvas()
            tooltip.dataset.flipX = String(event.point.x > canvas.clientWidth - TOOLTIP_MARGIN.x)
            tooltip.dataset.flipY = String(event.point.y > canvas.clientHeight - TOOLTIP_MARGIN.y)
          }

          const site =
            hit === undefined || typeof hit.properties.cluster_id === 'number'
              ? null
              : readHoveredSite(hit.properties)

          // Only a change of site costs a render; following the cursor does not.
          setHovered((current) => (current?.id === site?.id ? current : site))
        })

        map.on('mouseout', () => setHovered(null))
      })
      .catch((cause: unknown) => {
        console.error('map: maplibre-gl could not be loaded', cause)
        if (!cancelled) setEngineFailed(true)
      })

    return () => {
      cancelled = true
      created?.remove()
      mapRef.current = null
      handleRef.current = null
      setEngineReady(false)
      setHovered(null)
    }
  }, [setSelectedSiteId])

  // ------------------------------------------------------------------------
  // Filters changed: push the new set into the existing source, in place.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (map === null || !engineReady) return
    setStationsData(map, toCollection(tally.features))
  }, [tally, engineReady])

  // ------------------------------------------------------------------------
  // Selection: highlight, then bring it into view.
  //
  // This is the whole of "map selection opens the detail panel" and "a search
  // result flies the map": both write `selectedSiteId`, and this effect is the
  // only thing that moves the camera in response.
  // ------------------------------------------------------------------------
  const easedToRef = useRef<string | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (map === null || !engineReady) return

    setSelectionFilter(map, selectionFilter(selectedSiteId))

    if (selectedSiteId === null) {
      easedToRef.current = null
      return
    }
    // Only chase a selection that actually changed - a data refresh must not
    // yank the camera back.
    if (easedToRef.current === selectedSiteId) return

    const target = index?.byId.get(selectedSiteId)
    if (target === undefined) return

    easedToRef.current = selectedSiteId
    easeToSite(map, { center: target.geometry.coordinates, reducedMotion })
  }, [selectedSiteId, engineReady, index, reducedMotion])

  // ------------------------------------------------------------------------
  // The blue dot.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (map === null || !engineReady) return
    setLocationData(map, position === null ? EMPTY_LOCATION : toLocationCollection(position))
  }, [position, engineReady])

  // ------------------------------------------------------------------------
  // The opening view: the visitor, with the nearest few chargers around them.
  //
  // Driven by the fix *count* rather than by the position, so pressing the
  // locate control a second time from the same spot still flies the camera -
  // the position has not changed, but the request has been made again.
  //
  // The first fix defers to a view somebody already chose: a `?site=` link is
  // about that site, and a camera the visitor has dragged is theirs. Later
  // fixes are explicit presses of the control and always move it.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (map === null || !engineReady || position === null || index === null) return
    if (fixes === framedFixesRef.current) return

    const firstFix = framedFixesRef.current === 0
    framedFixesRef.current = fixes
    if (firstFix && (selectedRef.current !== null || cameraTouchedRef.current)) return

    frameLocation(
      map,
      locationViewBounds(tallyRef.current.features, [position.lon, position.lat]),
      reducedMotion,
    )
  }, [fixes, position, engineReady, index, reducedMotion])

  // ------------------------------------------------------------------------
  // Colour scheme: swap the basemap style, then put our layers back.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (map === null || !engineReady || schemeRef.current === scheme) return
    schemeRef.current = scheme
    map.setStyle(resolveBasemapStyleUrl(scheme))
  }, [scheme, engineReady])

  // The canvas label and the data credit both follow the language picker.
  useEffect(() => {
    mapRef.current?.getCanvas().setAttribute('aria-label', strings.map.label)
  }, [strings.map.label])

  useEffect(() => {
    if (attributionRef.current === strings.map.dataAttribution) return
    attributionRef.current = strings.map.dataAttribution
    handleRef.current?.setDataAttribution(strings.map.dataAttribution)
  }, [strings.map.dataAttribution])

  // ------------------------------------------------------------------------
  // Publish results, so the filter panel never has to filter anything itself.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const failed = engineFailed || resource.status === 'error'
    const ready = resource.status === 'ready' && engineReady

    publishResults({
      status: failed ? 'error' : ready ? 'ready' : 'loading',
      total: index?.features.length ?? 0,
      matching: tally.features.length,
      visible,
      byOperator: tally.byOperator,
      byOperatorName: tally.byOperatorName,
      // Only claim to have published once there is a real answer behind it.
      matchingIds: resource.status === 'ready' ? tally.ids : null,
    })
  }, [publishResults, resource.status, engineFailed, engineReady, index, tally, visible])

  const onZoom = useCallback(
    (delta: 1 | -1) => {
      const map = mapRef.current
      if (map === null) return
      const options = { duration: reducedMotion ? 0 : 300 }
      if (delta === 1) map.zoomIn(options)
      else map.zoomOut(options)
    },
    [reducedMotion],
  )

  const onResetView = useCallback(() => {
    const map = mapRef.current
    if (map !== null) frameCountry(map, reducedMotion)
  }, [reducedMotion])

  /**
   * One path for both the first fix and every later press: ask for a position,
   * and let the framing effect react to the answer. A granted permission comes
   * back out of the browser's cache, so this is not a second GPS warm-up.
   */
  const onLocate = useCallback(() => requestLocation(), [requestLocation])

  const locationBlocked = locationState === 'unsupported' || locationState === 'denied'

  const busy = (!engineReady || resource.status === 'loading') && !engineFailed
  const failure = engineFailed ? null : resource.status === 'error' ? resource.failure : null
  const failed = engineFailed || failure !== null

  return (
    <div className="map" data-scheme={scheme}>
      <div className="map__canvas" ref={containerRef} />

      <MapTooltip site={hovered} scheme={scheme} elementRef={tooltipRef} />

      <div className="map__controls">
        <button
          type="button"
          className="map__control"
          onClick={() => onZoom(1)}
          title={strings.map.zoomIn}
          aria-label={strings.map.zoomIn}
        >
          <span aria-hidden="true">+</span>
        </button>
        <button
          type="button"
          className="map__control"
          onClick={() => onZoom(-1)}
          title={strings.map.zoomOut}
          aria-label={strings.map.zoomOut}
        >
          <span aria-hidden="true">−</span>
        </button>
        <button
          type="button"
          className="map__control"
          onClick={onLocate}
          disabled={locationBlocked}
          aria-busy={locationState === 'locating'}
          title={locationBlocked ? strings.map.locateUnavailable : strings.map.locate}
          aria-label={locationBlocked ? strings.map.locateUnavailable : strings.map.locate}
        >
          <span aria-hidden="true">◎</span>
        </button>
        <button
          type="button"
          className="map__control map__control--wide"
          onClick={onResetView}
          title={strings.map.resetView}
          aria-label={strings.map.resetView}
        >
          <span aria-hidden="true">{strings.map.resetViewShort}</span>
        </button>
      </div>

      <MapKeyboardList
        targets={targets}
        selectedId={selectedSiteId}
        scheme={scheme}
        onSelect={setSelectedSiteId}
        onClear={() => setSelectedSiteId(null)}
      />

      {/* Held in the tree from the first paint and faded out, so there is no
          empty frame and no spinner appearing after the fact. */}
      <div className="map__veil" data-visible={busy} aria-hidden={!busy}>
        <div className="map__veil-inner">
          <span className="map__veil-pulse" aria-hidden="true" />
          <p className="map__veil-text">
            {engineReady ? strings.common.loading : strings.map.preparing}
          </p>
        </div>
      </div>

      {failed && (
        <div className="map__error" role="alert">
          <p className="map__error-title">
            {failure === null ? strings.map.loadFailed : strings.dataHealth.failure[failure.kind]}
          </p>
          <p className="map__error-text">
            {failure === null
              ? strings.dataHealth.failureHint.unknown
              : strings.dataHealth.failureHint[failure.kind]}
          </p>
          {failure?.source != null && (
            <p className="map__error-source">{strings.dataHealth.failureSource(failure.source)}</p>
          )}
          {failure?.detail != null && (
            <details className="map__error-details">
              <summary>{strings.dataHealth.technicalDetail}</summary>
              <p>{failure.detail}</p>
            </details>
          )}
          <button type="button" className="ev-button" onClick={reload}>
            {strings.common.retry}
          </button>
        </div>
      )}

      <p className="visually-hidden" aria-live="polite">
        {strings.map.announce(visible, tally.features.length)}
      </p>

      {ignoredFacets.length > 0 && (
        <p className="visually-hidden" aria-live="polite">
          {strings.map.ignoredFacets(
            ignoredFacets.map((facet) => strings.filters[FACET_LABEL_KEYS[facet]]),
          )}
        </p>
      )}
    </div>
  )
}
